import {requiredListingFields} from "@hosttools/core/constant";
import {getListingName} from "@hosttools/core/shared/utils/utils.listing";
import {useCallback, useMemo} from "react";

import type {Listing} from "../../../models/listing";
import type {ListingGroup} from "../../../models/listingGroup";

interface UseDirectBookingValidationProps {
    listingID?: string;
    listingGroupID?: string;
    enabled?: boolean;
    listings: Listing[];
    listingGroups: ListingGroup[];
    isStripeConnected?: boolean;
}

export interface ListingValidationResult {
    isValid: boolean;
    missingFields: string[];
    errorMessage: string;
}

export interface ListingGroupValidationResult {
    isValid: boolean;
    invalidListings: Array<{
        listing: Listing;
        validation: ListingValidationResult;
    }>;
    errorMessage: string;
}

export interface ValidationError {
    message: string;
    type: "text" | "listing_group_links" | "listing_links";
    listingLinks?: Array<{
        listingId: string;
        listingName: string;
        url: string;
    }>;
}

/**
 * Validates if a listing has all required fields for direct booking
 */
export function validateListingRequiredFields(
    listing: Listing | undefined
): ListingValidationResult {
    if (!listing) {
        return {
            isValid: false,
            missingFields: [],
            errorMessage: "No listing selected"
        };
    }

    const missingFields = requiredListingFields.filter(field => {
        const value = listing[field as keyof Listing];

        // Check for null, undefined, empty string, or empty arrays
        if (value === null || value === undefined || value === "") {
            return true;
        }

        return false;
    });

    return {
        isValid: missingFields.length === 0,
        missingFields,
        errorMessage:
            missingFields.length > 0 ? `Missing required fields: ${missingFields.join(", ")}` : ""
    };
}

/**
 * Validates listings in a listing group for direct booking requirements
 */
export function validateListingGroupRequiredFields(
    listings: Listing[]
): ListingGroupValidationResult {
    const invalidListings = listings
        .map(listing => ({listing, validation: validateListingRequiredFields(listing)}))
        .filter(({validation}) => !validation.isValid);

    return {
        isValid: invalidListings.length === 0,
        invalidListings,
        errorMessage:
            invalidListings.length > 0
                ? `${invalidListings.length} listing(s) are missing required fields for direct booking`
                : ""
    };
}

/**
 * Comprehensive validation for direct booking configuration
 */
export function validateDirectBookingConfiguration({
    listingID,
    listingGroupID,
    enabled,
    listings,
    listingGroups,
    isStripeConnected
}: {
    listingID?: string;
    listingGroupID?: string;
    enabled?: boolean;
    listings: Listing[];
    listingGroups: ListingGroup[];
    isStripeConnected?: boolean;
}) {
    const result = {
        isValid: true,
        errors: [] as ValidationError[],
        canEnable: true
    };

    // Basic selection validation
    if (!listingID && !listingGroupID) {
        result.errors.push({
            message: "Either listing or listing group is required",
            type: "text"
        });
        result.isValid = false;
        result.canEnable = false;
    }

    // Only validate further if enabled or trying to enable
    if (enabled) {
        // Stripe validation
        if (!isStripeConnected) {
            result.errors.push({
                message: "Stripe must be connected to enable direct booking",
                type: "text"
            });
            result.canEnable = false;
        }

        // Listing validation
        if (listingID) {
            const selectedListing = listings.find(l => l._id === listingID);
            const validation = validateListingRequiredFields(selectedListing);
            if (!validation.isValid) {
                const listingName = selectedListing
                    ? getListingName(selectedListing)
                    : "Unknown listing";
                result.errors.push({
                    message:
                        "The selected listing is missing required fields. Edit listing content:",
                    type: "listing_links",
                    listingLinks: [
                        {
                            listingId: listingID,
                            listingName,
                            url: `/settings/listings/${listingID}`
                        }
                    ]
                });
                result.canEnable = false;
            }
        }

        // Listing group validation
        if (listingGroupID) {
            const selectedListingGroup = listingGroups.find(lg => lg._id === listingGroupID);
            if (selectedListingGroup) {
                const validation = validateListingGroupRequiredFields(
                    selectedListingGroup.listings
                );
                if (!validation.isValid) {
                    const listingLinks = validation.invalidListings.map(({listing}) => ({
                        listingId: listing._id,
                        listingName: getListingName(listing),
                        url: `/settings/listings/${listing._id}`
                    }));

                    result.errors.push({
                        message:
                            "Some listings in the selected listing group are missing required fields. Edit listing content:",
                        type: "listing_group_links",
                        listingLinks
                    });
                    result.canEnable = false;
                }
            }
        }
    }

    // Update overall validity
    result.isValid = result.errors.length === 0;

    return result;
}

export function useDirectBookingValidation({
    listingID,
    listingGroupID,
    enabled,
    listings,
    listingGroups,
    isStripeConnected
}: UseDirectBookingValidationProps) {
    const validation = useMemo(() => {
        return validateDirectBookingConfiguration({
            listingID,
            listingGroupID,
            enabled,
            listings,
            listingGroups,
            isStripeConnected
        });
    }, [listingID, listingGroupID, enabled, listings, listingGroups, isStripeConnected]);

    const getEnabledError = useCallback(() => {
        const error = validation.errors.find(
            error => error.type === "text" && error.message.includes("Stripe")
        );
        return error?.message;
    }, [validation]);

    const getSelectionError = useCallback(() => {
        const error = validation.errors.find(
            error =>
                error.type === "text" && error.message.includes("Either listing or listing group")
        );
        return error?.message;
    }, [validation]);

    const getListingErrorWithLinks = useCallback(() => {
        const error = validation.errors.find(error => error.type === "listing_links");
        if (!error) return null;

        return {
            message: error.message,
            listingLinks: error.listingLinks || []
        };
    }, [validation]);

    const getListingGroupErrorWithLinks = useCallback(() => {
        const error = validation.errors.find(error => error.type === "listing_group_links");
        if (!error) return null;

        return {
            message: error.message,
            listingLinks: error.listingLinks || []
        };
    }, [validation]);

    return {
        ...validation,
        getEnabledError,
        getSelectionError,
        getListingErrorWithLinks,
        getListingGroupErrorWithLinks
    };
}
